import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Utilidad de seguridad para inputs de texto libre.
 * Patrón alineado a Aranceles (`input-security.util.ts`).
 */
export class InputSecurityUtil {
  /** Secuencias típicas de XSS o inyección SQL; el resto de caracteres queda permitido. */
  static readonly UNSAFE_INPUT_PATTERNS: RegExp[] = [
    /<script[\s>]/i,
    /<\/script>/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<\s*iframe/i,
    /<\s*object/i,
    /<\s*embed/i,
    /data:text\/html/i,
    /\bselect\b[\s\S]*\bfrom\b/i,
    /\bselect\b[\s\S]*\*/i,
    /\bunion\b[\s\S]*\bselect\b/i,
    /\bdrop\b[\s\S]*\btable\b/i,
    /\btruncate\b[\s\S]*\btable\b/i,
    /\balter\b[\s\S]*\btable\b/i,
    /\binsert\b[\s\S]*\binto\b/i,
    /\bdelete\b[\s\S]*\bfrom\b/i,
    /\bupdate\b[\s\S]*\bset\b/i,
    /\bexec\b/i,
    /\bexecute\b/i,
    /\bxp_/i,
    /\bor\b\s+\d+\s*=\s*\d+/i,
    /'\s*;\s*--/i,
    /"\s*;\s*--/i,
    /;\s*--/,
  ];

  static hasUnsafeInput(value: unknown): boolean {
    if (value == null || value === '') {
      return false;
    }
    const text = String(value);
    return InputSecurityUtil.UNSAFE_INPUT_PATTERNS.some((pattern) => pattern.test(text));
  }

  static trimTextValue(value: unknown): string {
    if (value == null) {
      return '';
    }
    return String(value).trim();
  }

  /**
   * Campo opcional: vacío es válido.
   * Si hay texto (tras trim), exige min..max y contenido seguro.
   */
  static validateOptionalSecureText(
    value: unknown,
    minLength = 3,
    maxLength = 250
  ): 'empty' | 'unsafe' | 'min' | 'max' | 'ok' {
    const trimmed = InputSecurityUtil.trimTextValue(value);
    if (!trimmed) {
      return 'empty';
    }
    if (InputSecurityUtil.hasUnsafeInput(trimmed)) {
      return 'unsafe';
    }
    if (trimmed.length < minLength) {
      return 'min';
    }
    if (trimmed.length > maxLength) {
      return 'max';
    }
    return 'ok';
  }

  static noUnsafeInputValidator(control: AbstractControl): ValidationErrors | null {
    const trimmed = InputSecurityUtil.trimTextValue(control.value);
    if (InputSecurityUtil.hasUnsafeInput(trimmed)) {
      return { unsafeInput: true };
    }
    return null;
  }

  static noUnsafeInputValidatorFn(): ValidatorFn {
    return InputSecurityUtil.noUnsafeInputValidator;
  }

  /** Vacío OK; si hay valor tras trim, exige largo mínimo. */
  static optionalTrimmedMinLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const trimmed = InputSecurityUtil.trimTextValue(control.value);
      if (!trimmed) {
        return null;
      }
      if (trimmed.length < min) {
        return { minlength: { requiredLength: min, actualLength: trimmed.length } };
      }
      return null;
    };
  }

  /** Vacío OK; si hay valor tras trim, exige largo máximo. */
  static optionalTrimmedMaxLength(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const trimmed = InputSecurityUtil.trimTextValue(control.value);
      if (!trimmed) {
        return null;
      }
      if (trimmed.length > max) {
        return { maxlength: { requiredLength: max, actualLength: trimmed.length } };
      }
      return null;
    };
  }
}

export const UNSAFE_INPUT_PATTERNS = InputSecurityUtil.UNSAFE_INPUT_PATTERNS;
export const hasUnsafeInput = InputSecurityUtil.hasUnsafeInput.bind(InputSecurityUtil);
export const trimTextValue = InputSecurityUtil.trimTextValue.bind(InputSecurityUtil);
export const noUnsafeInputValidator = InputSecurityUtil.noUnsafeInputValidator.bind(InputSecurityUtil);
export const noUnsafeInputValidatorFn = InputSecurityUtil.noUnsafeInputValidatorFn.bind(InputSecurityUtil);
